import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  buildCptSearchCatalog,
  searchCptCatalog,
  type CptSearchContext,
  type CptSearchEntry,
} from '@/lib/cptSearch/catalog';

interface UseCptSearchOptions extends CptSearchContext {
  existingCodes?: Iterable<string>;
  limit?: number;
  enabled?: boolean;
}

function isNumericSearch(query: string): boolean {
  return /^\d+$/.test(query.trim());
}

function remoteEntryFromCatalog(row: {
  cpt: string;
  descriptor: string;
  category: string | null;
  specialty_tags?: string[] | null;
}): CptSearchEntry {
  return {
    code: row.cpt,
    description: row.descriptor,
    category: row.category,
    notes: (row.specialty_tags || []).join(' '),
    source: 'cpt_catalog',
    priority: 64,
  };
}

function remoteEntryFromProcedure(row: {
  cpt_code: string;
  name: string;
  specialty: string | null;
}): CptSearchEntry {
  return {
    code: row.cpt_code,
    description: row.name,
    category: row.specialty,
    notes: row.specialty || undefined,
    source: 'procedure',
    priority: 70,
  };
}

export function useCptSearch(query: string, options: UseCptSearchOptions = {}) {
  const {
    existingCodes,
    limit = 12,
    enabled = true,
    ...context
  } = options;
  const [remoteEntries, setRemoteEntries] = useState<CptSearchEntry[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedQuery = query.trim();

  useEffect(() => {
    if (!enabled || trimmedQuery.length < 2) {
      setRemoteEntries([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      setError(null);

      try {
        const numeric = isNumericSearch(trimmedQuery);
        const catalogQuery = numeric
          ? supabase
            .from('cpt_catalog')
            .select('cpt, descriptor, category, specialty_tags')
            .ilike('cpt', `${trimmedQuery}%`)
            .limit(25)
          : supabase
            .from('cpt_catalog')
            .select('cpt, descriptor, category, specialty_tags')
            .ilike('descriptor', `%${trimmedQuery}%`)
            .limit(25);

        const procedureQuery = numeric
          ? supabase
            .from('procedures')
            .select('cpt_code, name, specialty')
            .eq('active', true)
            .ilike('cpt_code', `${trimmedQuery}%`)
            .limit(25)
          : supabase
            .from('procedures')
            .select('cpt_code, name, specialty')
            .eq('active', true)
            .ilike('name', `%${trimmedQuery}%`)
            .limit(25);

        const [catalogResult, procedureResult] = await Promise.all([
          catalogQuery,
          procedureQuery,
        ]);

        if (cancelled) return;

        const nextRemoteEntries: CptSearchEntry[] = [
          ...(procedureResult.data || []).map(remoteEntryFromProcedure),
          ...(catalogResult.data || []).map(remoteEntryFromCatalog),
        ];

        setRemoteEntries(nextRemoteEntries);
        const firstError = procedureResult.error || catalogResult.error;
        setError(firstError?.message || null);
      } catch (err) {
        if (cancelled) return;
        setRemoteEntries([]);
        setError(err instanceof Error ? err.message : 'CPT search failed');
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, trimmedQuery]);

  const catalog = useMemo(
    () => buildCptSearchCatalog({ ...context, remoteEntries }),
    [context.authorizedLines, context.template, context.primaryCpt, remoteEntries],
  );

  const results = useMemo(
    () => searchCptCatalog(catalog, trimmedQuery, { existingCodes, limit }),
    [catalog, existingCodes, limit, trimmedQuery],
  );

  return {
    catalog,
    results,
    isSearching,
    error,
  };
}
